// Pure helpers for the Reports area and the Today widgets (unit-tested in test/analytics.test.tsx).
import { formatMoney } from "@ady/format";
import { getDistrict } from "@ady/geo";
import { i18n, t } from "@ady/i18n";
import { ApiError } from "../api/client";
import type { BreakdownRow, Dim, WidgetId } from "./api";
import { courierName } from "../fulfilment/ui";
import { methodLabel } from "../orders/ui";

const CHANNELS = ["web", "pos", "manual", "headless", "marketplace", "ai"] as const;
const SOURCES = [
  "direct",
  "google",
  "facebook",
  "instagram",
  "tiktok",
  "youtube",
  "whatsapp",
  "bing",
] as const;
const DEVICES = ["mobile", "tablet", "desktop", "unknown"] as const;

/** One figure in its unit: paisa as money, basis points as a percentage, minutes as hours. */
export function formatFigure(metric: string, value: number | string | null | undefined): string {
  const n = typeof value === "number" ? value : Number(value ?? 0) || 0;
  if (metric.endsWith("_paisa")) return formatMoney(n);
  if (metric.endsWith("_bp")) {
    const pct = n / 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  if (metric === "average_delivery_minutes")
    return n >= 120
      ? t("analytics.units.hours", { value: String(Math.round(n / 6) / 10) })
      : t("analytics.units.minutes", { value: String(n) });
  return n.toLocaleString("en-IN");
}

export const metricLabel = (metric: string): string =>
  i18n.tDynamic(`analytics.metrics.${metric}`, "analytics.reports.key");

/** The display name of a breakdown key (server label first, then the client's own names). */
export function keyLabel(dim: Dim, row: Pick<BreakdownRow, "key" | "label">): string {
  if (row.label) return row.label;
  if (dim === "coupon" && row.key === "none") return t("analytics.keys.noCoupon");
  if (dim === "source")
    return (SOURCES as readonly string[]).includes(row.key)
      ? t(`analytics.keys.source.${row.key as (typeof SOURCES)[number]}`)
      : row.key;
  if (dim === "device")
    return t(
      `analytics.keys.device.${(DEVICES as readonly string[]).includes(row.key) ? (row.key as (typeof DEVICES)[number]) : "unknown"}`,
    );
  if (dim === "page" && row.key === "/") return t("analytics.keys.home");
  if (dim === "courier")
    return row.key === "none" ? t("analytics.keys.noCourier") : courierName(row.key);
  if (row.key === "none" || row.key === "unknown" || row.key === "online")
    return t(`analytics.keys.${row.key}`);
  if (dim === "method") return methodLabel(row.key);
  if (dim === "district") return getDistrict(row.key)?.nameEn ?? row.key;
  if (dim === "channel" && (CHANNELS as readonly string[]).includes(row.key))
    return t(`analytics.keys.channel.${row.key as (typeof CHANNELS)[number]}`);
  if (dim === "channel") return row.key.charAt(0).toUpperCase() + row.key.slice(1);
  return row.key;
}

/** True for the 451 entitlement answer (advanced analytics needed). */
export const isPlanLocked = (e: unknown): boolean =>
  e instanceof ApiError && e.status === 451 && e.code === "entitlement.flag_off";

/** The headline figures each topic shows first. */
export const HEADLINES: Readonly<Record<string, readonly string[]>> = {
  sales: ["net_paisa", "orders", "average_order_paisa", "collected_paisa", "outstanding_paisa"],
  products: ["units", "gross_paisa", "orders"],
  customers: ["customers", "new_customers", "returning_customers", "repeat_customers"],
  payments: ["net_paisa", "collected_paisa", "outstanding_paisa", "refunded_paisa"],
  returns: ["returns", "return_rate_bp", "rto", "rto_rate_bp", "return_value_paisa"],
  delivery: [
    "shipments",
    "delivered",
    "rto_rate_bp",
    "delivery_rate_bp",
    "average_delivery_minutes",
  ],
  traffic: [
    "page_view",
    "product_view",
    "add_to_cart",
    "checkout_start",
    "purchase",
    "conversion_bp",
  ],
  profit: ["revenue_paisa", "cogs_paisa", "gross_profit_paisa", "margin_bp"],
  inventory: [
    "stock_value_cost_paisa",
    "stock_units",
    "units_sold",
    "low_stock_variants",
    "out_of_stock_variants",
  ],
  discounts: ["discount_paisa", "discounted_orders", "discount_rate_bp", "coupon_orders"],
};

// ------------------------------------------------------------------ widget layout editing

export function moveWidget(list: readonly WidgetId[], id: WidgetId, by: -1 | 1): WidgetId[] {
  const i = list.indexOf(id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= list.length) return [...list];
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}
export const addWidget = (list: readonly WidgetId[], id: WidgetId): WidgetId[] =>
  list.includes(id) ? [...list] : [...list, id];
export const removeWidget = (list: readonly WidgetId[], id: WidgetId): WidgetId[] =>
  list.filter((w) => w !== id);

/** YYYY-MM-DD in Nepal time (UTC+05:45) for `ms`. */
export function nptToday(ms: number): string {
  return new Date(ms + (5 * 60 + 45) * 60_000).toISOString().slice(0, 10);
}
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}
